import {tokens} from '@pleo-io/telescope'
import styled, {css, keyframes} from 'styled-components'

import numberOneTrophy from '@/images/icons/number-one-trophy.svg'
import {CheckmarkList} from '@/styles/lists'
import {Text, StyledText, text} from '@helios/shared/styles/typography'
import {colors, fonts, radii, zIndex} from '@helios/shared/theme'
import mq from '@helios/shared/utils/media-query'

import {Props} from './pricing'

export const Row = styled.div(() => {
    return css`
        position: relative;
        display: grid;
        grid-template-columns: 1fr;
        column-gap: ${tokens.spacing20};

        ${mq.min('sm')} {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    `
})

export const Box = styled.div`
    position: relative;
    padding-right: ${tokens.spacing20};
    padding-left: ${tokens.spacing20};
    background-color: ${colors.background.default};

    ${mq.max('sm')} {
        ${Row}:nth-of-type(2) & {
            padding-top: ${tokens.spacing20};
        }

        ${Row}:last-of-type & {
            padding-bottom: ${tokens.spacing20};
        }
    }

    ${mq.min('sm')} {
        ${Row}:first-of-type & {
            padding-top: ${tokens.spacing40};
        }

        ${Row}:last-of-type & {
            padding-bottom: ${tokens.spacing40};
        }
    }

    ${mq.min('lg')} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

export const TiersTitle = styled(Box)`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
`

const numCogTeeth = 14

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }

  ${Array.from(
      Array(numCogTeeth),
      (_, i) => css`
          ${(i + 1) * (100 / numCogTeeth)}% {
              transform: rotate(${(i + 1) * (360 / numCogTeeth)}deg);
          }
      `
  )}
`

export const BadgeWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${zIndex.pricingPromoBadge};
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translateY(-80%);

    ${mq.min('sm')} {
        right: ${tokens.spacing20};
    }

    ${mq.min('lg')} {
        right: ${tokens.spacing40};
    }
`

export const BadgeText = styled(Text)`
    position: absolute;
    padding: ${tokens.spacing12};
    text-align: center;
    transform: rotate(10deg);

    ${mq.min('xs')} {
        padding: ${tokens.spacing24};
    }
`

export const Badge = styled.img`
    width: 100px;
    height: 100px;
    animation-name: ${spin};
    animation-duration: 10s;
    animation-timing-function: ease;
    animation-iteration-count: infinite;

    ${mq.min('xs')} {
        width: 120px;
        height: 120px;
    }

    ${mq.min('sm')} {
        width: 140px;
        height: 140px;
    }
`

export const Title = styled(Row)`
    ${Box} {
        align-items: center;
        display: flex;
        padding-bottom: ${tokens.spacing12};

        :nth-child(2) {
            position: relative;

            :after {
                position: absolute;
                top: -${tokens.spacing20};
                right: ${tokens.spacing20};
                width: 80px;
                height: 80px;
                content: '';
                background-color: ${colors.background.default};
                background-image: url(${numberOneTrophy});
                background-repeat: no-repeat;
                background-position: center;
                background-size: 80%;
                border-radius: ${radii.arc8};
                box-shadow: ${tokens.shadowRaiseSubtle};

                ${mq.min('lg')} {
                    right: ${tokens.spacing40};
                }
            }
        }
    }

    ${mq.max('sm')} {
        display: none;
    }
`

export const Excerpt = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing32};

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing20};
        }
    }
`

export const MobileNewTierBadge = styled(Row)`
    display: grid;
    padding-bottom: ${tokens.spacing10};
    background: ${colors.background.default};
    grid-template-columns: auto;
    justify-content: left;

    ${mq.min('sm')} {
        display: none;
    }
`

export const NewTierBadge = styled(Text)`
    padding: ${tokens.spacing4} ${tokens.spacing8};
    background: ${tokens.pink300};
    border-radius: ${radii.arc4};

    ${text({size: 'xs', font: 'secondary'})}
`

type AmountProps = Pick<Props, 'withCtas'>

export const Amount = styled(Row)<AmountProps>`
    ${Box} {
        display: grid;
        align-content: start;
        padding-bottom: ${tokens.spacing32};

        ${mq.min('sm')} {
            padding-bottom: ${(props) => (props.withCtas ? tokens.spacing20 : tokens.spacing40)};
        }
    }
`

export const Cta = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing32};

        > * {
            width: 100%;

            ${mq.min('sm')} {
                width: auto;
            }
        }

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing40};
        }
    }
`

export const FeaturesTitle = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing12};

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing20};
        }
    }
`

export const OuterWrapper = styled.div`
    position: relative;
`

interface WrapperProps {
    activeItemIndex: number
}

export const Wrapper = styled.div<WrapperProps>`
    ${mq.max('sm')} {
        ${Box}:not(:nth-of-type(${(props) => props.activeItemIndex + 1})) {
            display: none;
        }
    }
`

export const Tabs = styled.nav`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: ${tokens.spacing8};
    gap: ${tokens.spacing8};

    ${mq.min('sm')} {
        display: none;
    }
`

const isActive = css`
    ${StyledText} {
        font-weight: ${fonts.weights.semiBold};
        text-decoration: underline;
    }
`

interface TabProps {
    isActive: boolean
}

export const Tab = styled.button<TabProps>`
    ${text({color: 'default'})}
    padding: ${tokens.spacing20} 0;
    cursor: pointer;
    background: ${colors.background.default};
    border: none;

    ${(props) => props.isActive && isActive}
`

export const FeaturesList = styled(CheckmarkList)`
    color: ${colors.text.muted};
`
